import { useState, useEffect, useRef } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { Route } from '@/routes/_authenticated/setup/roles';
import { Plus, Shield, ShieldCheck, Pencil } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { MODULE_PERMISSIONS } from '@vagusx/shared';
import type { RoleResponse } from '@vagusx/clients';
import { TablePage, SortableHeader } from '@/components/table-page';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/custom/save-button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { SheetActions, SheetBody, SheetCancel } from '@/components/custom/sheet-layout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  useRoles,
  useRole,
  useAvailablePermissions,
  useCreateRole,
  useUpdateRole,
  useToggleRoleStatus,
} from '@/hooks/use-roles';

// Column definitions
const columns: ColumnDef<RoleResponse>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        {row.original.isSystem ? (
          <ShieldCheck className="h-4 w-4 text-primary" />
        ) : (
          <Shield className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="font-medium">{row.original.name}</span>
        {row.original.isSystem && (
          <Badge variant="secondary" className="text-xs">
            System
          </Badge>
        )}
      </div>
    ),
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) => (
      <span className="text-muted-foreground">{row.original.description ?? '-'}</span>
    ),
  },
  {
    accessorKey: 'rowStatus',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={row.original.rowStatus === 0 ? 'default' : 'secondary'}>
        {row.original.rowStatus === 0 ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
];

export default function RolesPage(): React.ReactElement {
  const { id: selectedRoleId } = Route.useSearch();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  const { data: roles, isLoading, error } = useRoles();
  const toggleStatus = useToggleRoleStatus();

  const handleRoleSelect = (role: RoleResponse) => {
    navigate({ to: '/setup/roles', search: { id: role.id }, replace: true });
  };

  const handleCloseSheet = () => {
    navigate({ to: '/setup/roles', search: {}, replace: true });
    setIsCreating(false);
  };

  return (
    <TablePage
      title="Roles"
      description="Manage roles and permissions for your organization"
      columns={columns}
      data={roles ?? []}
      total={roles?.length || undefined}
      isLoading={isLoading}
      error={error}
      searchColumn="name"
      searchPlaceholder="Search roles..."
      onRowClick={handleRoleSelect}
      emptyMessage="No roles found."
      errorMessage="Failed to load roles."
      headerAction={
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Role
        </Button>
      }
    >
      {/* Create Role Sheet */}
      <Sheet open={isCreating} onOpenChange={(open) => !open && setIsCreating(false)}>
        <SheetContent className="w-[540px] sm:max-w-[540px]">
          <RoleEditor onClose={() => setIsCreating(false)} />
        </SheetContent>
      </Sheet>

      {/* View/Edit Role Sheet */}
      <Sheet
        open={!!selectedRoleId && !isCreating}
        onOpenChange={(open) => !open && handleCloseSheet()}
      >
        <SheetContent className="w-[540px] sm:max-w-[540px]">
          {selectedRoleId && (
            <RoleDetailView
              roleId={selectedRoleId}
              onClose={handleCloseSheet}
              onToggleStatus={(id) => toggleStatus.mutate(id)}
            />
          )}
        </SheetContent>
      </Sheet>
    </TablePage>
  );
}

// ---------------------------------------------------------------------------
// Role Detail View
// ---------------------------------------------------------------------------

function RoleDetailView({
  roleId,
  onClose,
  onToggleStatus,
}: {
  roleId: string;
  onClose: () => void;
  onToggleStatus: (id: string) => void;
}): React.ReactElement {
  const { data, isLoading } = useRole(roleId);
  const [isEditing, setIsEditing] = useState(false);

  if (isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const { role, permissions: rolePerms } = data;

  if (isEditing && !role.isSystem) {
    return <RoleEditor roleId={role.id} onClose={() => setIsEditing(false)} />;
  }

  // Group permissions by module
  const permsByModule = new Map<string, typeof rolePerms>();
  for (const p of rolePerms) {
    const existing = permsByModule.get(p.module) ?? [];
    existing.push(p);
    permsByModule.set(p.module, existing);
  }

  // Build slug → label lookup from MODULE_PERMISSIONS
  const permLabelMap = new Map<string, string>();
  for (const mod of MODULE_PERMISSIONS) {
    for (const perm of mod.permissions) {
      permLabelMap.set(perm.permission, perm.label);
    }
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          {role.name}
          {role.isSystem && (
            <Badge variant="secondary" className="text-xs">
              System
            </Badge>
          )}
        </SheetTitle>
        <SheetDescription>{role.description ?? 'No description'}</SheetDescription>
      </SheetHeader>

      <SheetBody>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <Badge variant={role.rowStatus === 0 ? 'default' : 'secondary'}>
              {role.rowStatus === 0 ? 'Active' : 'Inactive'}
            </Badge>
          </div>

          <Separator />

          <h3 className="text-sm font-medium">Permissions ({rolePerms.length})</h3>

          <div className="space-y-4">
            {MODULE_PERMISSIONS.map((mod) => {
              const modulePerms = permsByModule.get(mod.module);
              if (!modulePerms || modulePerms.length === 0) return null;
              return (
                <div key={mod.module}>
                  <h4 className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                    {mod.label}
                  </h4>
                  <div className="space-y-1">
                    {modulePerms.map((p) => (
                      <div
                        key={`${p.module}:${p.action}`}
                        className="flex items-center justify-between rounded bg-muted/50 px-2 py-1 text-sm"
                      >
                        <span>
                          {permLabelMap.get(`${p.module}:${p.action}`) ?? `${p.module}:${p.action}`}
                        </span>
                        {p.scope && (
                          <Badge variant="outline" className="text-xs">
                            {p.scope === 'all' ? 'All records' : 'Own records'}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SheetBody>

      {!role.isSystem && (
        <SheetActions
          start={
            <Button
              variant={role.rowStatus === 0 ? 'destructive' : 'default'}
              onClick={() => onToggleStatus(role.id)}
            >
              {role.rowStatus === 0 ? 'Deactivate' : 'Activate'}
            </Button>
          }
        >
          <SheetCancel />
          <Button onClick={() => setIsEditing(true)}>
            <Pencil className="mr-2 h-3.5 w-3.5" />
            Edit
          </Button>
        </SheetActions>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Role Editor (Create / Edit)
// ---------------------------------------------------------------------------

interface PermissionSelection {
  permissionId: string;
  permission: string; // "module:action"
  scope: 'all' | 'own' | null;
  enabled: boolean;
}

/**
 * Build permission selection map from available permissions and existing role permissions.
 */
function buildPermSelections(
  availablePerms: Array<{ id: string; module: string; action: string }>,
  existingPerms?: Array<{ module: string; action: string; scope: string | null }>
): Map<string, PermissionSelection> {
  const map = new Map<string, PermissionSelection>();
  for (const perm of availablePerms) {
    const key = `${perm.module}:${perm.action}`;
    const existing = existingPerms?.find(
      (p) => p.module === perm.module && p.action === perm.action
    );
    map.set(key, {
      permissionId: perm.id,
      permission: key,
      scope: (existing?.scope as 'all' | 'own' | null) ?? null,
      enabled: !!existing,
    });
  }
  return map;
}

function RoleEditor({
  roleId,
  onClose,
}: {
  roleId?: string;
  onClose: () => void;
}): React.ReactElement {
  const isEdit = !!roleId;
  const { data: existingRole } = useRole(roleId);
  const { data: permData, isLoading: permLoading } = useAvailablePermissions();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ name: string; description: string }>({
    defaultValues:
      isEdit && existingRole
        ? {
            name: existingRole.role.name,
            description: existingRole.role.description ?? '',
          }
        : { name: '', description: '' },
  });

  // Build permission selection state
  const [permSelections, setPermSelections] = useState<Map<string, PermissionSelection>>(new Map());
  const initialized = useRef(false);

  useEffect(() => {
    if (!permData) return;
    if (isEdit && !existingRole) return;
    if (initialized.current) return;
    initialized.current = true;
    setPermSelections(buildPermSelections(permData.permissions, existingRole?.permissions));
  }, [permData, existingRole, isEdit]);

  const togglePermission = (key: string) => {
    setPermSelections((prev) => {
      const next = new Map(prev);
      const curr = next.get(key);
      if (curr) {
        next.set(key, { ...curr, enabled: !curr.enabled });
      }
      return next;
    });
  };

  const setScope = (key: string, scope: 'all' | 'own') => {
    setPermSelections((prev) => {
      const next = new Map(prev);
      const curr = next.get(key);
      if (curr) {
        next.set(key, { ...curr, scope });
      }
      return next;
    });
  };

  const onSubmit = async (formData: { name: string; description: string }) => {
    const selectedPerms = [...permSelections.values()]
      .filter((p) => p.enabled)
      .map((p) => ({ permissionId: p.permissionId, scope: p.scope }));

    if (isEdit && roleId) {
      await updateRole.mutateAsync({
        id: roleId,
        data: {
          name: formData.name,
          description: formData.description || undefined,
          permissions: selectedPerms,
        },
      });
    } else {
      const slug =
        formData.name
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '') || 'untitled';
      await createRole.mutateAsync({
        name: formData.name,
        description: formData.description || undefined,
        slug,
        permissions: selectedPerms,
      });
    }
    onClose();
  };

  if (permLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Loading permissions...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
      <SheetHeader>
        <SheetTitle>{isEdit ? 'Edit Role' : 'Create Role'}</SheetTitle>
        <SheetDescription>
          {isEdit
            ? 'Update role name, description, and permissions.'
            : 'Define a custom role with specific permissions.'}
        </SheetDescription>
      </SheetHeader>

      <SheetBody>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" {...register('name', { required: 'Name is required' })} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" rows={2} {...register('description')} />
          </div>

          <Separator />

          <h3 className="text-sm font-medium">Permissions</h3>

          <div className="space-y-5">
            {MODULE_PERMISSIONS.map((mod) => (
              <div key={mod.module}>
                <h4 className="mb-2 text-xs font-medium uppercase text-muted-foreground">
                  {mod.label}
                </h4>
                <div className="space-y-2">
                  {mod.permissions.map((modPerm) => {
                    const sel = permSelections.get(modPerm.permission);
                    if (!sel) return null;
                    return (
                      <div
                        key={modPerm.permission}
                        className="flex items-center justify-between gap-3 rounded border px-3 py-2"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <Checkbox
                            className="shrink-0"
                            checked={sel.enabled}
                            onCheckedChange={() => togglePermission(modPerm.permission)}
                          />
                          <span className="truncate text-sm">{modPerm.label}</span>
                        </div>
                        {modPerm.scopeable && sel.enabled && (
                          <div className="flex shrink-0 items-center gap-1 text-xs">
                            <button
                              type="button"
                              className={`rounded px-2 py-0.5 ${sel.scope === 'all' ? 'bg-highlight text-highlight-foreground' : 'bg-muted'}`}
                              onClick={() => setScope(modPerm.permission, 'all')}
                            >
                              All
                            </button>
                            <button
                              type="button"
                              className={`rounded px-2 py-0.5 ${sel.scope === 'own' ? 'bg-highlight text-highlight-foreground' : 'bg-muted'}`}
                              onClick={() => setScope(modPerm.permission, 'own')}
                            >
                              Own
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </SheetBody>

      <SheetActions>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {isEdit ? (
          <SaveButton type="submit" isPending={updateRole.isPending} isEdit />
        ) : (
          <Button type="submit" disabled={createRole.isPending}>
            Create Role
          </Button>
        )}
      </SheetActions>
    </form>
  );
}
